<template>
    <div class="bg-slate-100 min-h-screen">
        <div
            class="relative z-10 lg:hidden"
            aria-modal="true"
        >
            <Transition>
                <div
                    v-if="mobileMenuOpen"
                    class="fixed inset-0 bg-gray-900/80"
                    @click="mobileMenuOpen = false"
                />
            </Transition>

            <div
                class="mr-16 fixed inset-y-0 z-20 flex transition-all w-full max-w-xs flex-1"
                :class="{
                    'translate-x-0': mobileMenuOpen,
                    '-translate-x-full': !mobileMenuOpen
                }"
            >
                <Transition>
                    <div
                        v-if="mobileMenuOpen"
                        class="absolute left-full top-0 flex w-16 justify-center pt-5"
                        @click="mobileMenuOpen = false"
                    >
                        <button
                            type="button"
                            class="-m-2.5 p-2.5"
                        >
                            <span class="sr-only">Close sidebar</span>
                            <XMarkIconMini class="h-6 w-6 text-white" />
                        </button>
                    </div>
                </Transition>

                <!-- Sidebar component, swap this element with another sidebar if you like -->
                <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                    <div class="flex mt-20 shrink-0 items-center">
                        <img
                            class="w-auto"
                            src="https://abcreche.com/img/logo.png"
                            alt="ABCreche"
                        >
                    </div>
                    <nav
                        class="flex flex-1 flex-col"
                        aria-label="primary-navigation"
                    >
                        <ul class="flex flex-1 flex-col gap-y-7">
                            <li>
                                <CrecheNavigation />
                            </li>
                            <li class="mt-auto space-y-2">
                                <nuxt-link
                                    to="/tablet"
                                    class="nav-item"
                                >
                                    <DeviceTabletIconSolid class="icon" />
                                    Mode Tablette
                                </nuxt-link>
                                <nuxt-link
                                    v-if="$user?.organization"
                                    to="/organization"
                                    class="nav-item"
                                >
                                    <BuildingOffice2IconMini class="icon" />
                                    Gestion centralisée
                                </nuxt-link>
                                <CrecheSwitcher v-if="$creche" />
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>

        <div
            class="hidden lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:w-64 lg:flex-col"
        >
            <div
                class="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 px-6 pb-4 bg-cover bg-center bg-no-repeat"
                style="background-image: url('/nav-bg-rotated.png')"
            >
                <div class="flex mt-4 shrink-0 items-center">
                    <nuxt-link to="/">
                        <img
                            class="w-auto"
                            src="https://abcreche.com/img/logo.png"
                            alt="ABCreche"
                        >
                    </nuxt-link>
                </div>
                <nav
                    class="flex flex-1 flex-col"
                    aria-label="primary-navigation"
                >
                    <ul class="flex flex-1 flex-col gap-y-7">
                        <li>
                            <CrecheNavigation />
                        </li>

                        <li class="mt-auto space-y-2">
                            <nuxt-link
                                to="/tablet"
                                class="nav-item"
                            >
                                <DeviceTabletIconSolid class="icon" />
                                Mode Tablette
                            </nuxt-link>
                            <nuxt-link
                                v-if="$user?.organization"
                                to="/organization"
                                class="nav-item"
                            >
                                <Icon
                                    name="ph:buildings-fill"
                                    class="icon"
                                />
                                Gestion centralisée
                            </nuxt-link>
                            <CrecheSwitcher />
                        </li>
                    </ul>
                </nav>
            </div>
        </div>

        <div class="lg:pl-64">
            <div class="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                <button
                    type="button"
                    class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                    @click="mobileMenuOpen = !mobileMenuOpen"
                >
                    <span class="sr-only">Open sidebar</span>
                    <Bars3IconSolid class="h-6 w-6" />
                </button>

                <!-- Separator -->
                <div
                    class="h-6 w-px bg-gray-200 lg:hidden"
                    aria-hidden="true"
                />

                <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                    <CrecheTopSearch />
                    <div class="flex items-center space-x-4">
                        <QuickCreateMenu />
                        <UserNotifications />
                        <UserMessages />

                        <div class="h-16 flex items-center support-modal">
                            <UiButtonModal
                                id="support-modal"
                                size="5xl"
                            >
                                <template #trigger>
                                    <button
                                        type="button"
                                        class="text-gray-400 relative transition-colors hover:text-gray-500 group"
                                    >
                                        <LifebuoyIconOutline class="size-6" />
                                    </button>
                                </template>
                                <AppSupport />
                            </UiButtonModal>
                        </div>

                        <div class="h-16 flex items-center support-modal">
                            <UiButtonModal
                                id="albus-quick-look"
                                size="7xl"
                                no-background
                            >
                                <template #trigger>
                                    <button
                                        type="button"
                                        class="text-primary-400 flex items-center relative transition-colors hover:text-purple-500 group"
                                    >
                                        <Icon
                                            name="lucide:wand-sparkles"
                                            class="size-5"
                                        />
                                    </button>
                                </template>
                                <AlbusChat />
                            </UiButtonModal>
                        </div>

                        <!-- Separator -->
                        <div
                            class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
                            aria-hidden="true"
                        />

                        <!-- Profile dropdown -->
                        <UserDropdown v-if="$user" />
                    </div>
                </div>
            </div>

            <main>
                <div class="p-3 sm:p-4 lg:p-6">
                    <div class="relative">
                        <suspense>
                            <slot />
                        </suspense>
                    </div>
                </div>
            </main>
            <MobilePopupHandler />
            <UiDialog />
            <UiToastWrapper />
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia'
import { useAuthStore } from '~~/stores/auth'

export default {
    data () {
        return {
            mobileMenuOpen: false
        }
    },

    computed: {
        ...mapState(useAuthStore, ['user'])
    },

    watch: {
        $route () {
            this.mobileMenuOpen = false
        }
    }
}
</script>
